import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { login as apiLogin } from "../api/auth";
import { ApiError } from "../api/client";
import { getSessionContext, type SessionContext } from "../api/sessionContext";
import { loadSession, saveSession, clearSession, type StoredSession } from "./session";
import { mfaClient, setMfaClientSession } from "../api/mfaAuthClient";
import { normalizeAalLevel, type AalState } from "./aal";

type AuthState =
  | { status: "unauthenticated" }
  | {
      status: "authenticated";
      accessToken: string;
      /** Necessário só para MfaEnrollPage "emprestar" a sessão ao mfaClient (ver api/mfaAuthClient.ts) —
       *  não é usado em nenhuma chamada fetch-puro do resto do app. */
      refreshToken: string;
      memberId: string | null;
      username: string;
      email?: string;
      requiresPasswordChange: boolean;
      /** Fase F1 (ADR-054): true quando o papel exige TOTP e a sessão ainda não tem fator verificado. */
      requiresMfaEnrollment: boolean;
      aal: AalState;
      sessionContext: SessionContext | null;
      sessionContextStatus: "loading" | "ready" | "unavailable";
    };

interface AuthContextValue {
  state: AuthState;
  /** true quando a sessão caiu por expiração/erro 401 (não por logout manual) — pra avisar o associado. */
  sessionExpiredNotice: boolean;
  login: (username: string, password: string) => Promise<{ requiresPasswordChange: boolean; requiresMfaEnrollment: boolean }>;
  logout: () => void;
  /** Chamado por qualquer tela ao receber 401 numa chamada autenticada — derruba a sessão local. */
  expireSession: () => void;
  /**
   * Chamado por MfaEnrollPage só depois que verify() do GoTrue confirmou o fator e
   * record_mfa_enrollment_confirmed já foi gravada — nunca antes disso (@F1-ENROLL-004). Atualiza a
   * sessão local (persistida e em memória) sem exigir novo login, mesmo padrão de "servidor já
   * confirmou o fato, cliente só reflete" usado no resto do módulo.
   *
   * Achado do Codex (26/08/2026): verify() promove a sessão do GoTrue para AAL2 e devolve um novo par de
   * tokens — só zerar o booleano requiresMfaEnrollment (como a versão anterior fazia) deixava o resto do
   * app usando o JWT antigo (AAL1). accessToken/refreshToken são substituídos aqui pelos tokens novos.
   */
  applyMfaVerifiedSession: (tokens: { accessToken: string; refreshToken: string; expiresIn?: number }) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function sessionEffectIsCurrent(cancelled: boolean, accessToken: string): boolean {
  return !cancelled && loadSession()?.session.access_token === accessToken;
}

function toState(stored: StoredSession): AuthState {
  return {
    status: "authenticated",
    accessToken: stored.session.access_token,
    refreshToken: stored.session.refresh_token,
    memberId: stored.memberId,
    username: stored.username,
    email: stored.session.user.email,
    requiresPasswordChange: stored.requiresPasswordChange,
    requiresMfaEnrollment: stored.requiresMfaEnrollment,
    aal: { currentLevel: "aal1", nextLevel: "aal1", status: "loading", elevatedAt: null },
    sessionContext: null,
    sessionContextStatus: "loading",
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = loadSession();
    return stored ? toState(stored) : { status: "unauthenticated" };
  });
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(false);

  const login = useCallback(async (username: string, password: string) => {
    const response = await apiLogin(username, password);
    const stored: StoredSession = {
      session: response.session,
      memberId: response.user.member_id,
      username,
      requiresPasswordChange: response.requires_password_change,
      requiresMfaEnrollment: response.requires_mfa_enrollment,
    };
    saveSession(stored);
    setSessionExpiredNotice(false);
    setState(toState(stored));
    return { requiresPasswordChange: response.requires_password_change, requiresMfaEnrollment: response.requires_mfa_enrollment };
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setSessionExpiredNotice(false);
    setState({ status: "unauthenticated" });
  }, []);

  const expireSession = useCallback(() => {
    clearSession();
    setSessionExpiredNotice(true);
    setState({ status: "unauthenticated" });
  }, []);

  const applyMfaVerifiedSession = useCallback(({ accessToken, refreshToken, expiresIn }: { accessToken: string; refreshToken: string; expiresIn?: number }) => {
    const stored = loadSession();
    if (stored) {
      saveSession({
        ...stored,
        session: {
          ...stored.session,
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: expiresIn,
          expires_at: expiresIn ? Math.floor(Date.now() / 1000) + expiresIn : stored.session.expires_at,
        },
        requiresMfaEnrollment: false,
      });
    }
    setState((current) =>
      current.status === "authenticated"
        ? { ...current, accessToken, refreshToken, requiresMfaEnrollment: false, aal: { currentLevel: "aal2", nextLevel: "aal2", status: "ready", elevatedAt: Date.now() } }
        : current,
    );
  }, []);

  const refreshAal = useCallback(async (accessToken: string) => {
    const { data, error } = await mfaClient.auth.mfa.getAuthenticatorAssuranceLevel(accessToken);
    if (error || !data) throw error ?? new Error("AAL indisponível");
    setState((current) => current.status === "authenticated" && current.accessToken === accessToken
      ? { ...current, aal: { currentLevel: normalizeAalLevel(data.currentLevel), nextLevel: normalizeAalLevel(data.nextLevel), status: "ready", elevatedAt: current.aal.elevatedAt } }
      : current);
  }, []);

  useEffect(() => {
    if (state.status !== "authenticated") return;

    let cancelled = false;
    const accessToken = state.accessToken;

    if (state.requiresPasswordChange) {
      setState((current) => current.status === "authenticated" && current.accessToken === accessToken
        ? { ...current, aal: { ...current.aal, status: "unavailable" }, sessionContextStatus: "unavailable" }
        : current);
      return;
    }

    void setMfaClientSession(state.accessToken, state.refreshToken).then(() => refreshAal(accessToken)).catch((error: unknown) => {
      if (!sessionEffectIsCurrent(cancelled, accessToken)) return;
      if (error && typeof error === "object" && "status" in error && error.status === 401) {
        expireSession();
        return;
      }
      setState((current) => current.status === "authenticated" && current.accessToken === accessToken
        ? { ...current, aal: { ...current.aal, status: "unavailable" } }
        : current);
    });

    void (async () => {
      try {
        const sessionContext = await getSessionContext(accessToken);
        if (cancelled) return;
        setState((current) =>
          current.status === "authenticated" && current.accessToken === accessToken
            ? { ...current, sessionContext, sessionContextStatus: "ready" }
            : current,
        );
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          expireSession();
          return;
        }
        setState((current) =>
          current.status === "authenticated" && current.accessToken === accessToken
            ? { ...current, sessionContext: null, sessionContextStatus: "unavailable" }
            : current,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    expireSession,
    refreshAal,
    state.status === "authenticated" ? state.accessToken : null,
    state.status === "authenticated" ? state.requiresPasswordChange : null,
  ]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, sessionExpiredNotice, login, logout, expireSession, applyMfaVerifiedSession }),
    [state, sessionExpiredNotice, login, logout, expireSession, applyMfaVerifiedSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <AuthProvider>.");
  return ctx;
}
